import { FormEvent, useMemo, useState } from "react";
import { LinkedCategoryPicker, Option } from "./linked-category-picker";

export interface ServiceItemFormValues {
  serviceId: number;
  serviceTypeId: number;
  price: number;
  unitId?: number;
  weightPerUnit?: number;
  /** CSV id loại đồ liên kết, "" = không liên kết. */
  linkedCategoryIds: string;
}

interface Props {
  mode: "create" | "edit";
  /** Nhóm dịch vụ (services). */
  groupOptions: Option[];
  /** Dịch vụ (service_types) — label nên kèm mã để dễ tìm. */
  serviceTypeOptions: Option[];
  unitOptions: Option[];
  /** Chỉ để hiện tên cho id loại đồ đã liên kết; việc chọn đi qua ô tìm kiếm. */
  clothingCategoryOptions?: Option[];
  onSearchClothingCategories?: (keyword: string) => Promise<Option[]>;
  initialValues?: Partial<ServiceItemFormValues>;
  onClose: () => void;
  onSubmit: (values: ServiceItemFormValues) => Promise<boolean> | boolean;
  isSubmitting?: boolean;
}

const formatMoneyInput = (raw: string): string => {
  const digits = (raw || "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const stripToNumber = (val: string): number => {
  const digits = (val || "").replace(/\D/g, "");
  return digits ? Number(digits) : NaN;
};

/** Bỏ dấu + chữ thường để gõ "chan ga" ra "Chăn - Ga". */
const normalizeText = (s: string): string =>
  (s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase();

const MAX_SUGGESTIONS = 30;

/**
 * Thêm / sửa MỘT dịch vụ trong bảng giá (1 dòng service_type_maps).
 * Sửa: nhóm và dịch vụ chỉ hiển thị, không đổi được (đơn cũ lưu id dòng giá — đổi sẽ làm đơn cũ sai
 * thứ đã bán). Muốn đổi: tạm ngưng dòng cũ rồi thêm dòng mới.
 */
export const ServiceItemModal: React.FC<Props> = ({
  mode,
  groupOptions,
  serviceTypeOptions,
  unitOptions,
  clothingCategoryOptions = [],
  onSearchClothingCategories,
  initialValues,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const isEdit = mode === "edit";

  const [serviceId, setServiceId] = useState<string>(initialValues?.serviceId ? String(initialValues.serviceId) : "");
  const [serviceTypeId, setServiceTypeId] = useState<string>(
    initialValues?.serviceTypeId ? String(initialValues.serviceTypeId) : ""
  );
  const [price, setPrice] = useState<string>(
    initialValues?.price !== undefined ? formatMoneyInput(String(Math.round(initialValues.price))) : ""
  );
  const [unitId, setUnitId] = useState<string>(initialValues?.unitId ? String(initialValues.unitId) : "");
  const [weightPerUnit, setWeightPerUnit] = useState<string>(
    initialValues?.weightPerUnit ? String(initialValues.weightPerUnit) : ""
  );
  const [linkedIds, setLinkedIds] = useState<string[]>(
    (initialValues?.linkedCategoryIds || "").split(",").map((s) => s.trim()).filter(Boolean)
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  // Tên loại đồ vừa chọn qua ô tìm kiếm (danh sách tải sẵn có thể không chứa)
  const [extraLabels, setExtraLabels] = useState<Record<string, string>>({});

  // Ô chọn dịch vụ có lọc
  const [typeKeyword, setTypeKeyword] = useState("");
  const [typeListOpen, setTypeListOpen] = useState(false);

  const labelOfOption = (opts: Option[], id: string) => opts.find((o) => String(o.value) === id)?.label ?? "";
  const categoryLabel = (id: string) =>
    extraLabels[id] ?? (labelOfOption(clothingCategoryOptions, id) || `Loại đồ #${id}`);

  const typeSuggestions = useMemo(() => {
    const q = normalizeText(typeKeyword.trim());
    return serviceTypeOptions.filter((o) => !q || normalizeText(o.label).includes(q)).slice(0, MAX_SUGGESTIONS);
  }, [serviceTypeOptions, typeKeyword]);

  const toggleCategory = (id: string, label: string) => {
    setExtraLabels((prev) => ({ ...prev, [id]: label }));
    setLinkedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const priceNumber = stripToNumber(price);
  const canSubmit = !!serviceId && !!serviceTypeId && Number.isFinite(priceNumber) && priceNumber >= 0 && !isSubmitting; // cho phép giá 0 (dịch vụ miễn phí/tặng kèm)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const weight = parseFloat(weightPerUnit);
    const ok = await onSubmit({
      serviceId: Number(serviceId),
      serviceTypeId: Number(serviceTypeId),
      price: priceNumber,
      unitId: unitId ? Number(unitId) : undefined,
      weightPerUnit: Number.isFinite(weight) ? weight : undefined,
      linkedCategoryIds: linkedIds.join(","),
    });
    if (ok) onClose();
  };

  const inputClass =
    "w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-slate-100 disabled:text-slate-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={handleSubmit} className="w-full max-w-xl rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-800">{isEdit ? "Sửa dịch vụ" : "Thêm dịch vụ"}</h2>
          <button type="button" onClick={onClose} className="h-8 w-8 rounded-full text-xl text-slate-400 hover:bg-slate-100">
            ×
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          {/* Nhóm dịch vụ */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Nhóm dịch vụ <span className="text-rose-500">*</span>
            </label>
            <select
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
              disabled={isEdit}
              required
              className={inputClass}
            >
              <option value="">-- Chọn nhóm --</option>
              {groupOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {/* Dịch vụ */}
          <div className="relative space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Dịch vụ <span className="text-rose-500">*</span>
            </label>
            {isEdit ? (
              <input value={labelOfOption(serviceTypeOptions, serviceTypeId) || `Dịch vụ #${serviceTypeId}`} disabled className={inputClass} />
            ) : (
              <>
                <input
                  value={typeListOpen ? typeKeyword : labelOfOption(serviceTypeOptions, serviceTypeId)}
                  onChange={(e) => {
                    setTypeKeyword(e.target.value);
                    setTypeListOpen(true);
                  }}
                  onFocus={() => {
                    setTypeKeyword("");
                    setTypeListOpen(true);
                  }}
                  onBlur={() => setTypeListOpen(false)}
                  placeholder="Gõ tên hoặc mã dịch vụ để tìm..."
                  className={inputClass}
                />
                {typeListOpen && (
                  <div className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-lg">
                    {typeSuggestions.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-slate-400">Không tìm thấy dịch vụ phù hợp</div>
                    ) : (
                      typeSuggestions.map((o) => (
                        <button
                          key={o.value}
                          type="button"
                          // onMouseDown chạy trước onBlur của ô tìm — nếu dùng onClick danh sách đóng trước khi chọn
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setServiceTypeId(String(o.value));
                            setTypeListOpen(false);
                          }}
                          className={`block w-full px-4 py-2 text-left text-sm hover:bg-blue-50 ${
                            String(o.value) === serviceTypeId ? "bg-blue-50 font-medium text-blue-700" : "text-slate-700"
                          }`}
                        >
                          {o.label}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
            {isEdit && (
              <p className="text-xs text-slate-400">
                Không đổi được nhóm / dịch vụ. Muốn đổi: tạm ngưng dòng này rồi thêm dòng mới.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Giá */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Giá (VNĐ) <span className="text-rose-500">*</span>
              </label>
              <input
                value={price}
                onChange={(e) => setPrice(formatMoneyInput(e.target.value))}
                inputMode="numeric"
                placeholder="VD: 12,000"
                required
                className={`${inputClass} text-right`}
              />
            </div>

            {/* Đơn vị */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Đơn vị bán</label>
              <select value={unitId} onChange={(e) => setUnitId(e.target.value)} className={inputClass}>
                <option value="">-- Chọn đơn vị --</option>
                {unitOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Khối lượng quy đổi */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Khối lượng quy đổi 1 đơn vị (kg)</label>
            <input
              value={weightPerUnit}
              onChange={(e) => setWeightPerUnit(e.target.value.replace(/[^0-9.]/g, ""))}
              inputMode="decimal"
              placeholder="VD: 0.8 — dòng bán theo kg thì để 1"
              className={inputClass}
            />
          </div>

          {/* Loại đồ liên kết */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Loại đồ liên kết</label>
            <div className="flex flex-wrap items-center gap-2">
              {linkedIds.map((id) => (
                <span key={id} className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700">
                  {categoryLabel(id)}
                </span>
              ))}
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="rounded-full border border-dashed border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
              >
                {linkedIds.length ? "Sửa liên kết" : "+ Liên kết loại đồ"}
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            Hủy
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded-full bg-green-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-green-700 disabled:opacity-50"
          >
            {isSubmitting ? "Đang lưu..." : isEdit ? "Cập nhật" : "Thêm dịch vụ"}
          </button>
        </div>
      </form>

      {pickerOpen && (
        <LinkedCategoryPicker
          selectedIds={linkedIds}
          labelOf={categoryLabel}
          onSearch={onSearchClothingCategories}
          onToggle={toggleCategory}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
};

export default ServiceItemModal;
